import { Button, Modal, Paper, Slide, Typography } from '@material-ui/core'
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty'
import moment from 'moment'

import BookingConfirmed from '../../../assets/images/BookingConfirmed.png'
import BookingFailed from '../../../assets/images/BookingFailed.png'
import { gregorianToJalaaliWithHourMinuteAndSecond } from '../../../helpers/date'
import { addCommaToPrice } from '../../../helpers/numbers'
import { getDirection, getTranslate } from '../../../localization'
import { ReceiptDetails, ReceiptMode } from './receipt'
import { useStyles } from './styles/index.styles'

type ChargeRecieptProps = {
  mode: ReceiptMode
  open: boolean
  onClose: () => void
  details?: ReceiptDetails
  /** The page stopped checking while the payment is still pending. */
  stillProcessing?: boolean
}

const ChargeReciept: React.FC<ChargeRecieptProps> = ({
  mode,
  open,
  onClose,
  details,
  stillProcessing = false
}: ChargeRecieptProps) => {
  const classes = useStyles()

  const todayDate = moment().format('YYYY-MM-DD HH:mm:ss')

  return (
    <Modal open={open} onClose={onClose} dir={getDirection()}>
      <Slide direction="down" in={open}>
        <Paper
          className={classes.paper}
          role="dialog"
          aria-modal="true"
          aria-labelledby="charge-receipt-title"
          aria-describedby="charge-receipt-description"
          tabIndex={-1}>
          <div className={classes.main}>
            <div className={classes.header}>
              {/* Decorative: the title says the outcome. */}
              {mode === 'Pending' ? (
                <HourglassEmptyIcon
                  className={classes.img}
                  color="primary"
                  aria-hidden="true"
                />
              ) : (
                <img
                  src={mode === 'Failed' ? BookingFailed : BookingConfirmed}
                  className={classes.img}
                  alt=""
                />
              )}
              <div className={classes.right}>
                <div className={classes.headerRow}>
                  {/* Announced when a pending receipt turns successful. */}
                  <div role="status" aria-live="polite">
                    <Typography
                      id="charge-receipt-title"
                      className={classes.title}>
                      {mode === 'Successful' &&
                        getTranslate('Congratulation, Payment Was Successful')}
                      {mode === 'Failed' &&
                        getTranslate('Oop, Your Payment Failed')}
                      {mode === 'Pending' &&
                        getTranslate('We Received Your Payment')}
                    </Typography>
                    {mode === 'Pending' && (
                      <Typography
                        id="charge-receipt-description"
                        variant="h6"
                        className={classes.text}>
                        {stillProcessing
                          ? getTranslate(
                              'We are still processing this payment. Reload this page later to check, or contact support with the payment number below if your wallet is not credited within an hour.'
                            )
                          : getTranslate(
                              'Your wallet is usually credited within a few minutes, but it can take up to an hour. This page updates automatically. If your wallet is not credited after an hour, contact support with the payment number below.'
                            )}
                      </Typography>
                    )}
                  </div>
                  <div className={classes.date}>
                    <Typography variant="h6">
                      {`${getTranslate(
                        'Payment Date'
                      )}: ${gregorianToJalaaliWithHourMinuteAndSecond(
                        todayDate
                      )}`}
                    </Typography>
                  </div>
                </div>
                <div className={classes.headerRow}>
                  <div className={classes.wallet}>
                    {details?.paymentNumber && (
                      <div className={classes.textRow}>
                        <Typography variant="h5" className={classes.greyText}>
                          {`${getTranslate('Payment Number')}:`}
                        </Typography>
                        <Typography variant="h5" className={classes.text}>
                          <bdi>{details.paymentNumber}</bdi>
                        </Typography>
                      </div>
                    )}
                    {details?.amount && (
                      <div className={classes.textRow}>
                        <Typography variant="h5" className={classes.greyText}>
                          {`${getTranslate('Amount')}:`}
                        </Typography>
                        <Typography variant="h5" className={classes.text}>
                          <bdi>
                            {`${addCommaToPrice(details.amount)} ${
                              details.currency
                            }`.trim()}
                          </bdi>
                        </Typography>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className={classes.btnContainer}>
              <Button variant="text" className={classes.btn} onClick={onClose}>
                <Typography variant="h5" className={classes.redText}>
                  {getTranslate('Close')}
                </Typography>
              </Button>
            </div>
          </div>
        </Paper>
      </Slide>
    </Modal>
  )
}

export default ChargeReciept
